/**
 * Edição de um canal já criado.
 *
 * Existe porque o limite de participantes só podia ser decidido no momento da
 * criação. Quem errou a mão precisava apagar a sala e criar outra, perdendo
 * junto o histórico e o lugar dela na lista.
 */

import { useEffect, useState } from 'react'
import { LIMITS, type Channel } from '@nexus/shared'
import { api, ApiError } from '@/lib/api'
import { useAppStore } from '@/store/app'
import { Button, Field, Input, Modal, Textarea } from '@/components/ui'
import { ParticipantLimitField } from '@/components/layout/ParticipantLimitField'

export function EditChannelModal({
  channel,
  onClose,
}: {
  channel: Channel | null
  onClose: () => void
}) {
  const applyEvent = useAppStore((s) => s.applyEvent)

  const [nome, setNome] = useState('')
  const [topico, setTopico] = useState('')
  const [limite, setLimite] = useState(0)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (channel) {
      setNome(channel.name)
      setTopico(channel.topic ?? '')
      setLimite(channel.userLimit)
      setErro(null)
    }
  }, [channel])

  const salvar = async () => {
    if (!channel) return

    const limpo = nome.trim()
    if (limpo.length === 0) {
      setErro('Escreva um nome para o canal')
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      const atualizado = await api.channels.update(channel.id, {
        name: limpo,
        topic: topico.trim() || null,
        ...(channel.type === 'voice' ? { userLimit: limite } : {}),
      })
      applyEvent({ op: 'channel_update', d: atualizado, s: 0 })
      onClose()
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível salvar')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      open={channel !== null}
      onClose={onClose}
      title="Editar canal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} loading={salvando}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Nome" required error={erro ?? undefined}>
          <Input
            autoFocus
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void salvar()}
            maxLength={LIMITS.channelName.max}
          />
        </Field>

        <Field
          label="Assunto"
          hint="Aparece ao lado do nome, no alto da conversa. Serve para dizer para que o canal é."
        >
          <Textarea
            rows={2}
            value={topico}
            onChange={(e) => setTopico(e.target.value)}
            maxLength={LIMITS.topic.max}
            placeholder="Opcional"
          />
        </Field>

        {channel?.type === 'voice' ? (
          <Field label="Quantas pessoas cabem">
            <ParticipantLimitField valor={limite} onChange={setLimite} />
          </Field>
        ) : null}
      </div>
    </Modal>
  )
}
